import { useSearchParams } from 'react-router-dom';

/** Lê a página a partir de `URLSearchParams` — parsing defensivo (query param é input não-confiável). */
export function readPageParam(
  searchParams: URLSearchParams,
  paramName = 'page',
): number {
  const raw = Number(searchParams.get(paramName));
  return Number.isInteger(raw) && raw >= 1 ? raw : 1;
}

/** Aplica a página num `URLSearchParams` mutável — página 1 some da URL (canônica/limpa). */
export function writePageParam(
  params: URLSearchParams,
  page: number,
  paramName = 'page',
): void {
  if (page <= 1) params.delete(paramName);
  else params.set(paramName, String(page));
}

/**
 * Página atual de uma lista paginada, sincronizada com a URL (`?page=N`) em
 * vez de `useState`. Sobrevive a "Voltar" (nativo do browser ou
 * `useBackNavigate`) porque o componente remonta lendo o valor da própria URL,
 * em vez de reinicializar em 1 (primum-core-root/docs/plans/done/admin-dashboard-pagination-url-sync.md).
 *
 * **Cuidado ao combinar com outro setter de URL na mesma ação** (ex.: aplicar
 * filtro e resetar a página no mesmo clique): duas chamadas independentes de
 * `setSearchParams` no mesmo tick partem do mesmo `location.search` e a
 * segunda sobrescreve a primeira (react-router não fecha sobre o resultado da
 * primeira chamada). Nesse caso, não use este hook sozinho — monte a mudança
 * combinada num único `setSearchParams`, usando `readPageParam`/`writePageParam`
 * exportados aqui junto com o equivalente de `use-url-filters.ts`. Ver
 * `TotvsSyncTasksPage.tsx` como referência.
 *
 * @param paramName nome do query param — configurável para não colidir quando
 * duas listas paginadas coexistem na mesma rota (ex.: `?page=` vs `?events_page=`).
 */
export function usePageQueryParam(paramName = 'page') {
  const [searchParams, setSearchParams] = useSearchParams();

  const page = readPageParam(searchParams, paramName);

  const setPage = (next: number) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        writePageParam(params, next, paramName);
        return params;
      },
      { replace: true },
    );
  };

  return [page, setPage] as const;
}
